import { callExternalApi } from '../callExternalApi';

export interface CreateUploadURLResponse {
  url: string;
  docId: string;
}

export interface DocParserResponse {
  docId: string;
  agents: {
    parse: {
      model: string;
      prompt: string;
      response: string;
    };
    classify: {
      model: string;
      prompt: string;
      response: string;
    };
  };
  document: {
    type: string;
    json: string;
    supplierName: string;
    customerName: string;
    supplierId: string;
    customerId: string;
    suggestedType: string;
    text: string;
    schema: string;
  };
}

export interface DocParserHints {
  customerId?: string;
  supplierId?: string;
  quoteId?: string;
  customerName?: string;
  supplierName?: string;
  schema?: string;
  parsePrompt?: string;
}

export interface DocParserStatusResponse {
  tasks: Array<DocParserStatusResponseTask>;
}

export interface DocParserStatusResponseTask {
  task: string;
  start_time: number;
  elapsed_time?: number;
  content_length: number;
  status?: 'started' | 'processing_started' | 'complete' | 'error';
  error?: string;
}

// New types for async document parsing responses
export interface DocParserProcessingResponse {
  status: 'started' | 'in_progress' | 'error';
  docId: string;
  message: string;
}

// Union type for all possible responses from parseDocument
export type DocParserResult = DocParserResponse | DocParserProcessingResponse;

export type TaskStatus = 0 | 1 | 2 | 3; // 0: not started, 1: in progress, 2: completed, 3: error

export interface TaskCompletionStatus {
  reading: { status: TaskStatus; pages: number; pagesRead: number };
  docClassification: TaskStatus;
  parsing: TaskStatus;
  mapping: TaskStatus;
}

export type ProcessingStatus =
  | 'idle'
  | 'calculating_sha'
  | 'getting_upload_url'
  | 'uploading'
  | 'parsing'
  | 'completed'
  | 'error';

export const getUploadUrlApi = async (docId: string, contentType: string) => {
  return callExternalApi<CreateUploadURLResponse>({
    url: 'api/ai/doc/upload',
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: {
      docId,
      contentType
    }
  });
};

export const uploadFileApi = async (url: string, file: File) => {
  return await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });
};

export const parseDocumentApi = async (docId: string, ai_config_name: string, hints: DocParserHints = {}) => {
  return callExternalApi<DocParserResult>({
    url: 'api/ai/doc/parse',
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: {
      docId,
      ai_config_name,
      hints
    }
  });
};

export const getParseStatusApi = async (docId: string, ai_config_name: string, hints: DocParserHints = {}) => {
  return callExternalApi<DocParserStatusResponse>({
    url: 'api/ai/doc/status',
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: {
      docId,
      ai_config_name,
      hints
    }
  });
};
